"use client";

import {
  LightboxChrome,
  LightboxReadingPanel,
  getWidgetId,
  useLightbox,
} from "../LightboxProvider/LightboxProvider";
import { Maximize2, Minimize2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Button } from "../ui/button";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { createPortal } from "react-dom";

interface IframeLightboxProps {
  src: string;
  title?: string;
  allow?: string;
}

export default function IframeLightbox({
  src,
  title,
  allow,
}: IframeLightboxProps) {
  const {
    openSrc,
    pipSrc,
    open,
    close,
    enterPiP,
    exitPiP,
    readingMode,
    hoverReadingPanel,
  } = useLightbox();
  const isOpen = openSrc === src;
  const isPiP = pipSrc === src;

  const placeholderRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Portal target: the nearest scroll container, found on mount.
  // Rendering into it means position:absolute children scroll with the container
  // for free — no scroll listener needed.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  useEffect(() => {
    let el = placeholderRef.current?.parentElement ?? null;
    while (el) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === "auto" || overflowY === "scroll") {
        setPortalTarget(el);
        return;
      }
      el = el.parentElement;
    }
    setPortalTarget(document.body);
  }, []);

  // when lightbox opens, tell widget to start playing
  useEffect(() => {
    if (isOpen) {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "stone:open" },
        "*",
      );
      setTimeout(() => setHasBeenOpened(true), 0);
    }
  }, [isOpen]);

  // notify widget of pip state via postMessage (no src change = no reload)
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "stone:pip", active: isPiP },
      "*",
    );
  }, [isPiP]);

  // postMessage from widget → open lightbox
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === "stone:play" || e.data?.type === "stone:expand")
        open(src);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [src, open]);

  // mobile swipe-down when lightbox open → PiP
  useEffect(() => {
    if (!isOpen) return;
    let startY = 0;
    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.changedTouches[0].clientY - startY > 100 && window.innerWidth < 768)
        enterPiP(src);
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [isOpen, src, enterPiP]);

  // ── position sync ─────────────────────────────────────────────────────────
  // Inline: position:absolute with coords relative to the scroll container's
  // content origin. The element scrolls with the container naturally — no
  // scroll listener, no chasing, no lag.
  // Open/PiP: position:fixed via CSS class; clear inline styles.

  const syncPosition = useCallback(() => {
    if (!positionRef.current || !portalTarget) return;
    if (isOpen || isPiP) {
      const s = positionRef.current.style;
      s.removeProperty("top");
      s.removeProperty("left");
      s.removeProperty("width");
      s.removeProperty("height");
      return;
    }
    if (!placeholderRef.current) return;
    const r = placeholderRef.current.getBoundingClientRect();
    const cr = portalTarget.getBoundingClientRect();
    Object.assign(positionRef.current.style, {
      top: `${r.top - cr.top + portalTarget.scrollTop}px`,
      left: `${r.left - cr.left + portalTarget.scrollLeft}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
    });
  }, [isOpen, isPiP, portalTarget]);

  useLayoutEffect(() => {
    syncPosition();
  });

  useEffect(() => {
    if (isOpen || isPiP) return;
    window.addEventListener("resize", syncPosition);
    return () => window.removeEventListener("resize", syncPosition);
  }, [isOpen, isPiP, syncPosition]);

  return (
    <>
      {/* Inline spacer — holds space in the article flow */}
      <div
        ref={placeholderRef}
        id={getWidgetId(src)}
        data-stone-widget
        className="my-6 w-full aspect-video group"
      >
        {isPiP && (
          <div className="w-full h-full rounded-lg border border-dashed border-stone-300 dark:border-stone-600 flex flex-col items-center justify-center gap-2">
            <div className="flex items-center gap-3">
              <Image
                src="/static/stone_logo.svg"
                width={200}
                height={200}
                unoptimized
                className="w-10 dark:invert opacity-70"
                alt=""
              />
              <div className="h-3 w-3 rounded-full bg-green-400 animate-pulse" />
            </div>
            <span className="text-xs font-Cabin text-stone-500 dark:text-stone-400">
              Now playing
            </span>
            <button
              onClick={exitPiP}
              className="mt-1 flex items-center gap-1 text-[11px] font-Cabin text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors underline underline-offset-2"
            >
              <Minimize2 className="h-3 w-3" />
              Collapse
            </button>
          </div>
        )}
      </div>

      {/* Always-mounted portal into the scroll container — iframe never reloads */}
      {portalTarget &&
        createPortal(
          <div
            ref={positionRef}
            className={cn(
              isOpen
                ? "fixed inset-0 z-200"
                : isPiP
                ? "fixed bottom-4 right-4 w-56 h-32 z-150"
                : "absolute z-10",
              "group",
            )}
          >
            {isOpen && !readingMode && (
              <Button
                className={cn(
                  "absolute z-100 p-1 aspect-square",
                  "lg:w-[8dvh] lg:top-2 lg:right-2 lg:mt-12",
                  "right-2 top-9 mt-12 w-12",
                )}
                onClick={close}
                variant={"portalGhost"}
                size={"unset"}
                aria-label="Exit lightbox"
              >
                <Minimize2 className=" w-full h-full text-white" />
              </Button>
            )}

            <div
              className={cn(
                "w-full h-full overflow-hidden",
                isOpen
                  ? "bg-black/75 backdrop-blur-sm flex flex-col animate-in fade-in duration-200"
                  : isPiP
                  ? "rounded-xl shadow-2xl ring-1 ring-white/10"
                  : "rounded-lg",
              )}
            >
              {isOpen && <LightboxChrome />}

              {!isOpen && !isPiP && (
                <div
                  onClick={() => !hasBeenOpened && open(src)}
                  className={cn(
                    hasBeenOpened && "pointer-events-none",
                    "absolute inset-0 z-10 cursor-pointer",
                  )}
                  aria-label="Open widget fullscreen"
                />
              )}

              {isPiP && (
                <>
                  <div
                    onClick={() => open(src)}
                    className="absolute inset-0 z-10 cursor-pointer"
                    aria-label="Return to fullscreen"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      exitPiP();
                    }}
                    className="absolute top-1 right-1 z-20 bg-black/60 backdrop-blur-sm rounded-full p-0.5"
                    aria-label="Collapse picture-in-picture"
                  >
                    <Minimize2 className="h-3 w-3 text-white" />
                  </button>
                </>
              )}

              <div
                className={cn(isOpen ? "flex-1 min-h-0 flex" : "w-full h-full")}
              >
                <div
                  className={cn(isOpen ? "flex-1 min-w-0" : "w-full h-full")}
                >
                  {/* Size container: the 16:9 box below fits inside both
                      dimensions (like object-fit: contain), so a short
                      viewport letterboxes instead of cropping. */}
                  <div className="h-full flex items-center justify-center bg-black/50 [container-type:size]">
                    <div
                      className={cn(
                        "relative w-full h-full",
                        !readingMode &&
                          "md:h-auto md:w-[min(100cqw,100cqh*16/9)] md:aspect-video",
                      )}
                    >
                      <iframe
                        ref={iframeRef}
                        src={src}
                        title={title}
                        allow={allow}
                        className={cn(
                          "w-full h-full border-0 ",
                          isOpen ? "rounded-none" : "rounded-lg",
                        )}
                      />
                      {isOpen && readingMode && !hoverReadingPanel ? (
                        <Button
                          className="absolute group-hover:block hidden right-2 z-100 p-1 aspect-square w-[8dvh] top-2"
                          onClick={close}
                          variant={"portalGhost"}
                          size={"unset"}
                          aria-label="Exit lightbox"
                        >
                          <Minimize2 className=" w-full h-full text-white" />{" "}
                        </Button>
                      ) : !isPiP && hasBeenOpened && !isOpen ? (
                        <Button
                          className={cn(
                            "absolute group-hover:block lg:hidden aspect-square",
                            "lg:right-0 lg:p-1 lg:w-[6dvw] lg:top-0 lg:m-[3dvh]",
                            "w-100% top-3 right-3 mt-6",
                          )}
                          onClick={() => open(src)}
                          size={"unset"}
                          variant={"portalGhost"}
                          aria-label="Open lightbox"
                        >
                          <Maximize2 className=" w-full h-full text-white" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
                {isOpen && <LightboxReadingPanel />}
              </div>
            </div>
          </div>,
          portalTarget,
        )}
    </>
  );
}
